import type { QnAStateType, UserAnswerMapT } from '@uber/react-qna-lib';
import { DYNAMIC_ATTRIBUTE_LS_TAG, DYNAMIC_ATTRIBUTE_TAG, EMPTY_ARRAY } from '../../image/labelling/constants';
import { filterDynamicAnswers } from '../../image/labelling/reducers/utils/utils';
import type { AnswerT, DynamicAnnotationConfigT, GenericAnnotationT } from '../types/types';
import { getRandomId, insertIf } from '../utils/utils';
export function convertAnswersToUserAnswerMap(questionnaire: QnAStateType, answers: AnswerT): UserAnswerMapT {
  const newAnswers: UserAnswerMapT = {};
  for (let questionId of Object.keys(answers)) {
    newAnswers[questionId] = {
      ...answers[questionId],
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string | undefined' is not assignable to type 'string'.
      question: questionnaire.questions[questionId]?.question
    };
  }
  return newAnswers;
}
export const partitionStaticAndDynamicQuestionnaire = (response: QnAStateType) => {
  const partitionedQuestionnaire = Object.entries(response.questions).reduce<{
    staticQuestionnaire: QnAStateType;
    dynamicQuestionnaire: QnAStateType;
  }>((acc, [questionId, question]) => {
    const isDynamicField = question?.tags?.includes(DYNAMIC_ATTRIBUTE_LS_TAG) || question?.tags?.includes(DYNAMIC_ATTRIBUTE_TAG);
    if (isDynamicField) {
      acc.dynamicQuestionnaire = {
        ...acc.dynamicQuestionnaire,
        questions: {
          ...acc.dynamicQuestionnaire.questions,
          [questionId]: question
        },
        topQuestionIds: [...acc.dynamicQuestionnaire.topQuestionIds, questionId],
        // @ts-expect-error todo(ts-migration) TS2322 Type '{ [x: string]: UserAnswerItemT | undefined; }' is not assignable to type 'UserAnswerMapT'.
        answers: {
          ...acc.dynamicQuestionnaire.answers,
          ...insertIf({
            [questionId]: response.answers?.[questionId]
          }, !!response.answers?.[questionId])
        }
      };
    } else {
      acc.staticQuestionnaire = {
        ...acc.staticQuestionnaire,
        questions: {
          ...acc.staticQuestionnaire.questions,
          [questionId]: question
        },
        topQuestionIds: response.topQuestionIds.some(topQuestionId => topQuestionId === questionId) ? [...acc.staticQuestionnaire.topQuestionIds, questionId] : acc.staticQuestionnaire.topQuestionIds,
        // @ts-expect-error todo(ts-migration) TS2322 Type '{ [x: string]: UserAnswerItemT | undefined; }' is not assignable to type 'UserAnswerMapT'.
        answers: {
          ...acc.staticQuestionnaire.answers,
          ...insertIf({
            [questionId]: response.answers?.[questionId]
          }, !!response.answers?.[questionId])
        }
      };
    }
    return acc;
  }, {
    staticQuestionnaire: {
      questions: {},
      topQuestionIds: [],
      answers: {},
      modals: response.modals
    },
    dynamicQuestionnaire: {
      questions: {},
      topQuestionIds: [],
      answers: {},
      modals: response.modals
    }
  });
  return partitionedQuestionnaire;
};
export const adaptToFrameSpan = (range: {
  startFrameIndex: number;
  endFrameIndex: number;
}) => ({
  startFrame: range.startFrameIndex,
  endFrame: range.endFrameIndex
});
export const getExclusiveRanges = (currentRange: DynamicAnnotationConfigT, intersectingRange: DynamicAnnotationConfigT) => {
  const result: DynamicAnnotationConfigT[] = [];
  if (intersectingRange.frameStart <= currentRange.frameStart && intersectingRange.frameEnd >= currentRange.frameEnd) {
    return EMPTY_ARRAY;
  }
  if (intersectingRange.frameStart > currentRange.frameStart) {
    result.push({
      ...currentRange,
      frameEnd: intersectingRange.frameStart - 1
    });
  }
  if (intersectingRange.frameEnd < currentRange.frameEnd) {
    result.push({
      ...currentRange,
      frameStart: intersectingRange.frameEnd + 1
    });
  }
  return result;
};
export const parseLocallyStoredAnswers = (answersMap: string) => {
  try {
    return (JSON.parse(answersMap) as AnswerT);
  } catch (e) {
    return undefined;
  }
};
export const getAnswersStorageKey = (annotation: GenericAnnotationT) => {
  return `questionnaire-answers-${annotation.id}-${annotation.annotationLabel.title}`;
};
export const getNewDynamicQuestionnaire = (annotation: GenericAnnotationT, {
  frameStart,
  frameEnd
}: {
  frameStart: number;
  frameEnd: number;
}): DynamicAnnotationConfigT => {
  return {
    id: getRandomId(),
    isValid: true,
    annotation: {
      ...annotation,
      metadata: {
        ...annotation.metadata,
        answers: filterDynamicAnswers(annotation.metadata?.answers)
      }
    },
    frameStart,
    frameEnd,
    savedToStore: false
  };
};
export const dynamicAnnotationConfigFactory = (selectedAnnotationId: string, annotation: GenericAnnotationT, getSelectedAnnotationFrameRange?: (annotationId: string) => {
  startFrameIndex: number;
  endFrameIndex: number;
}) => {
  const selectedAnnotationFrameRange = getSelectedAnnotationFrameRange?.(selectedAnnotationId);
  if (!selectedAnnotationFrameRange) {
    return getNewDynamicQuestionnaire(annotation, {
      frameStart: 1,
      frameEnd: 1
    });
  }
  return getNewDynamicQuestionnaire(annotation, {
    frameStart: selectedAnnotationFrameRange.startFrameIndex + 1,
    frameEnd: selectedAnnotationFrameRange.endFrameIndex + 1
  });
};